import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Field, FieldArray, ErrorMessage } from 'formik';
import { ReactComponent as DeleteIcon } from 'assets/icons/trash.svg';
import { ReactComponent as DropdownIcon } from 'assets/icons/dropdown-arrow.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import LangSwitcher, { LocaleTypes } from '../Common/LangSwitcher';
import LogicCreator from '../LogicCreator/LogicCreator';
import Interpretations from '../Interpretations/Interpretations';
import { LocaleType, InterpretationType } from '../types';
import Prompt from 'components/Modal/Prompt';
import Checkbox from '../Common/Checkbox';

const Content = styled.div`
  background-color: #fafafa;
  min-height: 15rem;
  width: 100%;
  position: relative;
  border: 1px solid black;
  border-top: none;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  border-radius: 0 0 5px 5px;
`;

const WarnIcon = styled(WarningIcon)`
  margin-right: 0.5rem;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  margin: 1rem 0;
`;

const Header = styled.div<{ isOpen: boolean }>`
  height: 3rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgb(31, 93, 234);
  color: rgb(31, 93, 234);
  font-size: 1.25rem;
  border-radius: ${({ isOpen }) => (isOpen ? '5px 5px 0 0' : '5px')};
  background-color: white;
`;

const Title = styled.input`
  font-size: 1.125rem;
  border: none;
  border-bottom: 1px solid black;
  outline: none;
  width: 25%;
  background-color: #fafafa;
`;

const FieldWrapper = styled.div<{ margin?: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ margin }) => (margin ? margin : '1rem')};
`;

const Button = styled.button`
  height: 2rem;
  outline: none;
  border: none;
  background-color: rgb(31, 93, 234) !important;
  max-width: 10rem;
  margin: 1rem 0 0;
  color: white;
  border-radius: 5px;
  font-weight: 500;
  cursor: pointer;
`;

const ShortDescription = styled(Title)`
  font-size: 0.875rem;
  width: 50%;
`;

const LongDescription = styled.textarea`
  border: none;
  border-bottom: 1px solid black;
  outline: none;
  width: 50%;
  height: 5rem;
  min-height: unset !important;
  background-color: #fafafa;
`;

const Icons = styled.div`
  display: flex;
`;

const IconWrapper = styled.span`
  cursor: pointer;
`;

const StyledDropdownIcon = styled(DropdownIcon)<{ isOpen: boolean }>`
  transform: rotate(${({ isOpen }) => (isOpen ? '180deg' : '0')});
  transition: 0.3s;
  margin-right: 1rem;
`;

const Error = styled.span`
  color: red;
`;

interface Props {
  index: number;
  remove: (removeIndex: number) => void;
  title: LocaleType;
  errors?: any;
  touched?: any;
  interpretations: InterpretationType[];
  visibleIfType: 'build' | 'edit';
  questionsList: string[];
  isActive: boolean;
}

const Recommendation = ({
  index,
  remove,
  title,
  errors,
  interpretations,
  visibleIfType,
  questionsList,
  isActive,
}: Props) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isVisibleIf, setVisibleIf] = useState(false);
  const [isDeletePrompt, setIsDeletePrompt] = useState(false);
  const [currentLang, setLang] = useState<LocaleTypes>('en');

  const isLangError = (lang: LocaleTypes) => {
    if (!errors) return false;
    const isTitleErr = errors.title && errors.title[lang];
    const isShortDescErr = errors.shortDesc && errors.shortDesc[lang];
    const isLongDescErr = errors.longDesc && errors.longDesc[lang];

    const areInterpretationErrors =
      errors.interpretations &&
      errors.interpretations.some((interpretation: any) => {
        if (!interpretation) return false;

        const {
          interpretationText,
          recommendationText,
          title,
        } = interpretation;
        if (!recommendationText) {
          return (
            (interpretationText && interpretationText[lang]) ||
            (title && title[lang])
          );
        }
        return (
          (interpretationText && interpretationText[lang]) ||
          (recommendationText && recommendationText[lang]) ||
          (title && title[lang])
        );
      });

    return (
      isTitleErr || isShortDescErr || isLongDescErr || areInterpretationErrors
    );
  };

  const langErrors = {
    en: isLangError('en'),
    de: isLangError('de'),
    fr: isLangError('fr'),
    it: isLangError('it'),
  };

  return (
    <Container>
      <Prompt
        isOpen={isDeletePrompt}
        title="Are you sure you want to delete recommendation?"
        onCancel={() => setIsDeletePrompt(false)}
        onConfirm={() => {
          remove(index);
          setIsDeletePrompt(false);
        }}
      />
      <Header isOpen={isOpen}>
        <span>{title[currentLang] || 'Recomendation'}</span>

        <Icons>
          {errors && (
            <IconWrapper>
              <WarnIcon />
            </IconWrapper>
          )}
          <IconWrapper>
            <StyledDropdownIcon
              isOpen={isOpen}
              onClick={() => setIsOpen(!isOpen)}
            />
          </IconWrapper>

          <IconWrapper onClick={() => setIsDeletePrompt(true)}>
            <DeleteIcon />
          </IconWrapper>
        </Icons>
      </Header>

      {isOpen && (
        <Content>
          <LangSwitcher
            langErrors={langErrors}
            currentLang={currentLang}
            setLang={setLang}
          />

          {currentLang === 'en' && (
            <FieldWrapper>
              <Field
                name={`recommendations.${index}.title.en`}
                as={Title}
                placeholder="Title"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.title.en`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'de' && (
            <FieldWrapper>
              <Field
                name={`recommendations.${index}.title.de`}
                as={Title}
                placeholder="Title"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.title.de`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'fr' && (
            <FieldWrapper>
              <Field
                name={`recommendations.${index}.title.fr`}
                as={Title}
                placeholder="Title"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.title.fr`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'it' && (
            <FieldWrapper>
              <Field
                name={`recommendations.${index}.title.it`}
                as={Title}
                placeholder="Title"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.title.it`}
              />
            </FieldWrapper>
          )}

          {/* SHORT DESC */}
          {currentLang === 'en' && (
            <FieldWrapper margin="2rem">
              <Field
                name={`recommendations.${index}.shortDesc.en`}
                as={ShortDescription}
                placeholder="Short description"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.shortDesc.en`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'de' && (
            <FieldWrapper margin="2rem">
              <Field
                name={`recommendations.${index}.shortDesc.de`}
                as={ShortDescription}
                placeholder="Short description"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.shortDesc.de`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'fr' && (
            <FieldWrapper margin="2rem">
              <Field
                name={`recommendations.${index}.shortDesc.fr`}
                as={ShortDescription}
                placeholder="Short description"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.shortDesc.fr`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'it' && (
            <FieldWrapper margin="2rem">
              <Field
                name={`recommendations.${index}.shortDesc.it`}
                as={ShortDescription}
                placeholder="Short description"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.shortDesc.it`}
              />
            </FieldWrapper>
          )}

          {/* LONG DESC */}
          {currentLang === 'en' && (
            <FieldWrapper margin="2rem">
              <Field
                name={`recommendations.${index}.longDesc.en`}
                as={LongDescription}
                placeholder="Long description"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.longDesc.en`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'de' && (
            <FieldWrapper margin="2rem">
              <Field
                name={`recommendations.${index}.longDesc.de`}
                as={LongDescription}
                placeholder="Long description"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.longDesc.de`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'fr' && (
            <FieldWrapper margin="2rem">
              <Field
                name={`recommendations.${index}.longDesc.fr`}
                as={LongDescription}
                placeholder="Long description"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.longDesc.fr`}
              />
            </FieldWrapper>
          )}
          {currentLang === 'it' && (
            <FieldWrapper margin="2rem">
              <Field
                name={`recommendations.${index}.longDesc.it`}
                as={LongDescription}
                placeholder="Long description"
              />
              <ErrorMessage
                component={Error}
                name={`recommendations.${index}.longDesc.it`}
              />
            </FieldWrapper>
          )}

          <Button type="button" onClick={() => setVisibleIf(!isVisibleIf)}>
            Visible if
          </Button>
          {isVisibleIf && (
            <LogicCreator
              fieldName={`recommendations.${index}`}
              visibleIfType={visibleIfType}
              questionsList={questionsList}
            />
          )}
          <FieldArray name={`recommendations.${index}.interpretations`}>
            {({ remove, push }) => {
              return (
                <Interpretations
                  push={push}
                  remove={remove}
                  values={interpretations || []}
                  currentLang={currentLang}
                  name={`recommendations.${index}.interpretations`}
                  questionsList={questionsList}
                />
              );
            }}
          </FieldArray>
          <Checkbox
            isChecked={isActive}
            label="is Active"
            fieldName={`recommendations.${index}.isActive`}
          />
        </Content>
      )}
    </Container>
  );
};

export default Recommendation;
